import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useRounds } from '../hooks/useRounds';
import { useTeams } from '../hooks/useTeams';
import CountdownBar from './CountdownBar';
import MatchCard from './MatchCard';
import './GroupStageTab.css';

export default function GroupStageTab({ leagueId, uid }) {
  const rounds = useRounds();
  const teams  = useTeams();

  const groupRound  = rounds['group'];
  const roundStatus = groupRound?.status ?? 'pending';
  const postLock    = roundStatus === 'locked' || roundStatus === 'complete';

  // Real-time group stage matches
  const [matches, setMatches] = useState([]);
  useEffect(() => {
    const q = query(collection(db, 'matches'), where('round', '==', 'group'));
    return onSnapshot(q, (snap) => {
      const data = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.kickoffTime?.seconds ?? 0) - (b.kickoffTime?.seconds ?? 0));
      setMatches(data);
    }, (err) => console.error('matches subscription error:', err));
  }, []);

  // Real-time user picks for this league (group round only)
  const [userPicks, setUserPicks] = useState({}); // matchId → pick
  useEffect(() => {
    if (!uid || !leagueId) return;
    const q = query(
      collection(db, 'picks'),
      where('leagueId', '==', leagueId),
      where('userId', '==', uid)
    );
    return onSnapshot(q, (snap) => {
      const data = {};
      snap.docs.forEach((d) => {
        const pick = d.data();
        if (pick.round === 'group') data[pick.matchId] = { id: d.id, ...pick };
      });
      setUserPicks(data);
    }, (err) => console.error('picks subscription error:', err));
  }, [leagueId, uid]);

  // Group matches by group letter (A–L)
  const groupedMatches = useMemo(() => {
    const groups = {};
    for (const m of matches) {
      if (!m.group) continue;
      if (!groups[m.group]) groups[m.group] = [];
      groups[m.group].push(m);
    }
    return groups;
  }, [matches]);

  const sortedGroups = Object.keys(groupedMatches).sort();

  if (!matches.length) {
    return <div className="gst-loading">Loading matches…</div>;
  }

  return (
    <div className="gst">
      {!postLock && <CountdownBar />}

      {sortedGroups.map((groupKey) => (
        <div key={groupKey} className="gst-group">
          <div className="gst-group-title">Group {groupKey}</div>
          {groupedMatches[groupKey].map((match) => (
            <MatchCard
              key={match.id}
              match={match}
              homeTeam={teams[match.homeTeamId]}
              awayTeam={teams[match.awayTeamId]}
              userPick={userPicks[match.id] ?? null}
              roundStatus={roundStatus}
              leagueId={leagueId}
              uid={uid}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
